import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import "./pattern.less";
import { Reit, YearData, UnitHolderData } from "../../../../types/reit.types";

interface PatternProps {
  reit: Reit | null;
}

const Pattern: React.FC<PatternProps> = ({ reit }) => {
  if (!reit) return null;

  const [view, setView] = useState<"Yearly" | "Quarterly">("Yearly");
  const [year, setYear] = useState<string>(reit.years[0]?.year || "");
  const [quarter, setQuarter] = useState<string>("");
  const chartRef = useRef<SVGSVGElement | null>(null);

  const selectedYearData = useMemo(
    () => reit.years.find((y) => y.year === year),
    [reit, year]
  );

  const quarterOptions: string[] = useMemo(() => {
    const quarters = (selectedYearData as any)?.quarters || [];
    return quarters.map((q: any) => q.quarter);
  }, [selectedYearData]);

  const currentData: UnitHolderData | undefined = useMemo(() => {
    if (view === "Yearly") {
      return selectedYearData?.unitHolderData;
    }
    if (view === "Quarterly") {
      const quarters = (selectedYearData as any)?.quarters || [];
      if (!quarters.length) return undefined;
      const qData =
        quarters.find((q: any) => q.quarter === quarter) || quarters[0];
      return qData.unitHolderData;
    }
    return undefined;
  }, [view, selectedYearData, quarter]);

  const pieData = useMemo(() => {
    if (!currentData) return [];
    return [
      {
        label: "Sponsor & Sponsor Group",
        value: Number(currentData.sponsorGroup?.value ?? 0),
        color: "#1B324C",
      },
      {
        label: "Institutional",
        value: Number(currentData.institutionalVal?.value ?? 0),
        color: "#1F70CC",
      },
      {
        label: "Non-institutional",
        value: Number(currentData.nonInstitutionalVal?.value ?? 0),
        color: "#65D9D1",
      },
      {
        label: "Non-Sponsor Non-Public Holding",
        value: Number(currentData.nonSponsorNonPublicHoldingsVal?.value ?? 0),
        color: "#A5A5A5",
      },
    ];
  }, [currentData]);

  useEffect(() => {
    if (view === "Quarterly") {
      const quarters = (selectedYearData as any)?.quarters || [];
      if (quarters.length) {
        // if the current quarter doesn't exist in the new year, reset it
        const found = quarters.find((q: any) => q.quarter === quarter);
        if (!found) {
          setQuarter(quarters[0].quarter);
        }
      } else {
        setQuarter("");
      }
    }
  }, [year, view, selectedYearData]);

  const unitholders = {
    label: currentData?.unitHolders?.key || "Unitholders",
    value: currentData?.unitHolders?.value || "-",
  };

  useEffect(() => {
    if (!chartRef.current || pieData.length === 0) return;

    const svgElement = chartRef.current;
    const container = svgElement.parentElement;

    // ✅ Prevent null issues
    if (!container) return;

    const containerWidth = container.offsetWidth || 240;
    const containerHeight = container.offsetHeight || 240;

    const width = containerWidth;
    const height = containerHeight;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgElement);
    svg.selectAll("*").remove();

    svg.attr("width", width).attr("height", height);

    const g = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    const pie = d3
      .pie<{ label: string; value: number; color: string }>()
      .sort(null)
      .value((d) => d.value);

    const arc = d3
      .arc<d3.PieArcDatum<{ label: string; value: number; color: string }>>()
      .innerRadius(radius * 0.58)
      .outerRadius(radius * 0.92);

    const arcs = g
      .selectAll("path.slice")
      .data(pie(pieData))
      .enter()
      .append("path")
      .attr("class", "slice")
      .attr("d", arc as any)
      .attr("fill", (d) => d.data.color)
      .style("opacity", 0);

    arcs
      .transition()
      .duration(700)
      .delay((_, i) => i * 120)
      .style("opacity", 1);

    const centerText = view === "Yearly" ? `FY ${year}` : quarter || "";
    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "0.2em")
      .style("font-size", "14px")
      .style("font-weight", 700)
      .style("fill", "#3B3B3B")
      .style("font-family", "'Red Hat Text', sans-serif")
      .text(centerText);
  }, [pieData, view, year, quarter]);

  return (
    <section className="pattern">
      <div className="pattern__card">
        {/* Header */}
        <div className="pattern__header">
          <h2>Unitholding Pattern</h2>

          <div className="pattern__subheader">
            {/* Toggle */}
            <div className="pattern__tabs">
              <button
                className={view === "Yearly" ? "active" : ""}
                onClick={() => setView("Yearly")}
              >
                Yearly
              </button>
              <button
                className={view === "Quarterly" ? "active" : ""}
                onClick={() => {
                  setView("Quarterly");
                  if (quarterOptions.length) {
                    setQuarter(quarterOptions[0]); // default first quarter
                  }
                }}
              >
                Quarterly
              </button>
            </div>

            {/* Dropdowns container (Quarter → Year) */}
            <div className="pattern__dropdowns">
              {view === "Quarterly" && quarterOptions.length > 0 && (
                <div className="pattern__dropdown" style={{ marginRight: 8 }}>
                  <div className="custom-select">
                    <select
                      value={quarter}
                      onChange={(e) => setQuarter(e.target.value)}
                    >
                      {quarterOptions.map((q, idx) => (
                        <option key={idx} value={q}>
                          {q}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Year dropdown always last */}
              <div className="pattern__dropdown">
                <div className="custom-select">
                  <select
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {reit.years.map((y, idx) => (
                      <option key={idx} value={y.year}>
                        FY {y.year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="pattern__body">
          <div className="pattern__chart-box">
            <div className="pattern__chart-area">
              <svg ref={chartRef} />
            </div>

            {/* Legend */}
            <div className="pattern__legend">
              <div className="legend-item legend-item--unitholders">
                <div className="legend-text">
                  <span className="legend-label">{unitholders.label}:</span>
                  <span className="legend-value">{unitholders.value}</span>
                </div>
              </div>

              {pieData.map((p, i) => (
                <div className="legend-item" key={i}>
                  <span
                    className="legend-dot"
                    style={{ background: p.color }}
                    aria-hidden
                  />
                  <div className="legend-text">
                    <div className="legend-label">{p.label} (%)</div>
                    <div className="legend-value">{p.value.toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Table */}
          <div className="pattern__table">
            <table>
              <thead>
                <tr>
                  <th>Year</th>
                  <th>{view === "Yearly" ? `FY ${year}` : quarter}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{unitholders.label}</td>
                  <td>{unitholders.value}</td>
                </tr>
                {pieData.map((row, idx) => (
                  <tr key={idx}>
                    <td>{row.label}</td>
                    <td>{row.value.toFixed(2)} %</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pattern;
